import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShoppingCart, Zap, Eye, Users, RefreshCw, Play, Pause,
  AlertTriangle, TrendingUp, DollarSign, MousePointer,
  BarChart3, Target, ChevronDown, ChevronUp,
  Filter
} from 'lucide-react'
import { googleAdsApi, GoogleAdsCampaign } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

// Formatting helpers
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('es-ES').format(Math.round(value))
}

const formatPercent = (value: number) => {
  return `${value.toFixed(2)}%`
}

// Campaign type icons
const campaignTypeConfig: Record<string, { icon: any; color: string; label: string }> = {
  SHOPPING: {
    icon: ShoppingCart,
    color: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30',
    label: 'Shopping'
  },
  PERFORMANCE_MAX: {
    icon: Zap,
    color: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30',
    label: 'Performance Max'
  },
  SEARCH: {
    icon: Eye,
    color: 'text-primary-500 bg-blue-100 dark:bg-blue-900/30',
    label: 'Search'
  },
  REMARKETING: {
    icon: Users,
    color: 'text-success-500 bg-success-100 dark:bg-green-900/30',
    label: 'Remarketing'
  }
}

// Status styles
const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
  ENABLED: {
    bg: 'bg-success-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
    label: 'Activa'
  },
  PAUSED: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-300',
    label: 'Pausada'
  },
  REMOVED: {
    bg: 'bg-danger-100 dark:bg-danger-900/30',
    text: 'text-danger-700 dark:text-red-300',
    label: 'Eliminada'
  }
}

// Campaign Card Component
function CampaignCard({
  campaign,
  onPause,
  onEnable,
  isPausing,
  isEnabling,
  expanded,
  onToggle
}: {
  campaign: GoogleAdsCampaign
  onPause: () => void
  onEnable: () => void
  isPausing: boolean
  isEnabling: boolean
  expanded: boolean
  onToggle: () => void
}) {
  const typeConfig = campaignTypeConfig[campaign.campaign_type] || campaignTypeConfig.SEARCH
  const StatusIcon = typeConfig.icon
  const statusStyle = statusStyles[campaign.status] || statusStyles.PAUSED
  const metrics = campaign.metrics

  // Calculate ROAS if we have data
  const roas = metrics && metrics.cost > 0
    ? (metrics.conversion_value / metrics.cost) * 100
    : null

  return (
    <div className={cn(
      "bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden",
      "transition-all duration-200",
      expanded && "ring-2 ring-blue-500"
    )}>
      {/* Campaign Header */}
      <div
        className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-900/30"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={cn("p-2 rounded-lg", typeConfig.color)}>
              <StatusIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">
                {campaign.name}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={cn(
                  "px-2 py-0.5 rounded text-xs font-medium",
                  statusStyle.bg,
                  statusStyle.text
                )}>
                  {statusStyle.label}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {typeConfig.label}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Quick Stats */}
            {metrics && (
              <div className="hidden md:flex items-center gap-6 text-sm">
                <div className="text-center">
                  <div className="text-slate-500 dark:text-slate-400 text-xs">Gasto</div>
                  <div className="font-semibold text-slate-900 dark:text-white">
                    {formatCurrency(metrics.cost)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-slate-500 dark:text-slate-400 text-xs">Conversiones</div>
                  <div className="font-semibold text-slate-900 dark:text-white">
                    {formatNumber(metrics.conversions)}
                  </div>
                </div>
                {roas !== null && (
                  <div className="text-center">
                    <div className="text-slate-500 dark:text-slate-400 text-xs">ROAS</div>
                    <div className={cn(
                      "font-semibold",
                      roas >= 400 ? "text-success-600 dark:text-success-400" :
                      roas >= 200 ? "text-yellow-600 dark:text-yellow-400" :
                      "text-danger-600 dark:text-danger-400"
                    )}>
                      {formatPercent(roas)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2">
              {campaign.status === 'ENABLED' ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onPause(); }}
                  disabled={isPausing}
                  className="p-2 text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded-lg transition-colors disabled:opacity-50"
                  title="Pausar campana"
                >
                  <Pause className={cn("w-4 h-4", isPausing && "animate-pulse")} />
                </button>
              ) : campaign.status === 'PAUSED' ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onEnable(); }}
                  disabled={isEnabling}
                  className="p-2 text-success-600 hover:bg-success-100 dark:hover:bg-green-900/30 rounded-lg transition-colors disabled:opacity-50"
                  title="Activar campana"
                >
                  <Play className={cn("w-4 h-4", isEnabling && "animate-pulse")} />
                </button>
              ) : null}

              {expanded ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 space-y-4 border-t border-slate-200 dark:border-slate-700">
          {/* Budget */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Presupuesto diario</div>
              <div className="text-lg font-bold text-slate-900 dark:text-white">
                {formatCurrency(campaign.daily_budget)}
              </div>
            </div>
            {campaign.target_roas && (
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">ROAS objetivo</div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatPercent(campaign.target_roas * 100)}
                </div>
              </div>
            )}
          </div>

          {/* Detailed Metrics */}
          {metrics && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 mb-1">
                  <Eye className="w-4 h-4" />
                  <span className="text-xs font-medium">Impresiones</span>
                </div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatNumber(metrics.impressions)}
                </div>
              </div>

              <div className="bg-success-50 dark:bg-success-900/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-success-600 dark:text-success-400 mb-1">
                  <MousePointer className="w-4 h-4" />
                  <span className="text-xs font-medium">Clics</span>
                </div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatNumber(metrics.clicks)}
                </div>
              </div>

              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs font-medium">Gasto</span>
                </div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatCurrency(metrics.cost)}
                </div>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-1">
                  <ShoppingCart className="w-4 h-4" />
                  <span className="text-xs font-medium">Conversiones</span>
                </div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatNumber(metrics.conversions)}
                </div>
              </div>

              <div className="bg-pink-50 dark:bg-pink-900/20 rounded-lg p-3">
                <div className="flex items-center gap-2 text-pink-600 dark:text-pink-400 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-medium">Valor conv.</span>
                </div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatCurrency(metrics.conversion_value)}
                </div>
              </div>
            </div>
          )}

          {/* Calculated Metrics */}
          {metrics && metrics.impressions > 0 && (
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">CTR</div>
                <div className="font-bold text-slate-900 dark:text-white">
                  {formatPercent((metrics.clicks / metrics.impressions) * 100)}
                </div>
              </div>
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">CPC medio</div>
                <div className="font-bold text-slate-900 dark:text-white">
                  {metrics.clicks > 0 ? formatCurrency(metrics.cost / metrics.clicks) : '-'}
                </div>
              </div>
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Coste/Conv.</div>
                <div className="font-bold text-slate-900 dark:text-white">
                  {metrics.conversions > 0 ? formatCurrency(metrics.cost / metrics.conversions) : '-'}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function GoogleAdsCampaignsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [expandedCampaign, setExpandedCampaign] = useState<number | null>(null)
  const [pausingCampaign, setPausingCampaign] = useState<number | null>(null)
  const [enablingCampaign, setEnablingCampaign] = useState<number | null>(null)

  // Queries
  const statusQuery = useQuery({
    queryKey: ['google-ads-status'],
    queryFn: googleAdsApi.getStatus
  })

  const campaignsQuery = useQuery({
    queryKey: ['google-ads-campaigns', statusFilter],
    queryFn: () => googleAdsApi.getCampaigns(statusFilter),
    enabled: statusQuery.data?.connected === true
  })

  const performanceQuery = useQuery({
    queryKey: ['google-ads-performance'],
    queryFn: () => googleAdsApi.getPerformanceOverview(),
    enabled: statusQuery.data?.connected === true
  })

  // Mutations
  const pauseMutation = useMutation({
    mutationFn: (campaignId: number) => googleAdsApi.pauseCampaign(campaignId),
    onMutate: (campaignId) => setPausingCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-campaigns'] })
      toast.success('Campana pausada')
    },
    onError: (error: any) => {
      toast.error('Error pausando campana', error.response?.data?.detail || error.message)
    },
    onSettled: () => setPausingCampaign(null)
  })

  const enableMutation = useMutation({
    mutationFn: (campaignId: number) => googleAdsApi.enableCampaign(campaignId),
    onMutate: (campaignId) => setEnablingCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-campaigns'] })
      toast.success('Campana activada')
    },
    onError: (error: any) => {
      toast.error('Error activando campana', error.response?.data?.detail || error.message)
    },
    onSettled: () => setEnablingCampaign(null)
  })

  const isConnected = statusQuery.data?.connected === true
  const campaigns = campaignsQuery.data || []
  const performance = performanceQuery.data?.metrics

  // Summary stats
  const summaryStats = {
    totalCampaigns: campaigns.length,
    activeCampaigns: campaigns.filter(c => c.status === 'ENABLED').length,
    totalBudget: campaigns.reduce((sum, c) => sum + c.daily_budget, 0),
    totalSpend: performance?.cost || 0,
    totalConversions: performance?.conversions || 0,
    roas: performance && performance.cost > 0
      ? (performance.conversion_value / performance.cost) * 100
      : 0
  }

  if (!isConnected) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-600 dark:text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-yellow-800 dark:text-yellow-200 mb-2">
            Google Ads no conectado
          </h2>
          <p className="text-yellow-700 dark:text-yellow-300">
            Configura la conexion con Google Ads desde el dashboard principal para ver las campanas.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Campanas
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Gestiona y monitoriza todas tus campanas de Google Ads
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['google-ads-campaigns'] })}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Sincronizar
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <BarChart3 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {summaryStats.totalCampaigns}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Total campanas</div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success-100 dark:bg-green-900/30 rounded-lg">
              <Play className="w-5 h-5 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {summaryStats.activeCampaigns}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Activas</div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Target className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {formatCurrency(summaryStats.totalBudget)}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Presupuesto/dia</div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <DollarSign className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {formatCurrency(summaryStats.totalSpend)}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Gasto 30d</div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <ShoppingCart className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">
                {formatNumber(summaryStats.totalConversions)}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">Conversiones</div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
              <TrendingUp className="w-5 h-5 text-pink-600 dark:text-pink-400" />
            </div>
            <div>
              <div className={cn(
                "text-2xl font-bold",
                summaryStats.roas >= 400 ? "text-success-600 dark:text-success-400" :
                summaryStats.roas >= 200 ? "text-yellow-600 dark:text-yellow-400" :
                "text-danger-600 dark:text-danger-400"
              )}>
                {formatPercent(summaryStats.roas)}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">ROAS</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600 dark:text-slate-400">Filtrar por estado:</span>
        </div>
        <div className="flex gap-2">
          {[
            { value: undefined, label: 'Todas' },
            { value: 'ENABLED', label: 'Activas' },
            { value: 'PAUSED', label: 'Pausadas' },
          ].map((option) => (
            <button
              key={option.value || 'all'}
              onClick={() => setStatusFilter(option.value)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                statusFilter === option.value
                  ? "bg-primary-600 text-white"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Campaigns List */}
      {campaignsQuery.isLoading ? (
        <div className="p-8 text-center">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Cargando campanas...</p>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
          <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
            No hay campanas
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            {statusFilter
              ? `No se encontraron campanas con estado "${statusFilter}"`
              : 'No se encontraron campanas en tu cuenta de Google Ads'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => (
            <CampaignCard
              key={campaign.google_campaign_id}
              campaign={campaign}
              onPause={() => pauseMutation.mutate(campaign.google_campaign_id)}
              onEnable={() => enableMutation.mutate(campaign.google_campaign_id)}
              isPausing={pausingCampaign === campaign.google_campaign_id}
              isEnabling={enablingCampaign === campaign.google_campaign_id}
              expanded={expandedCampaign === campaign.google_campaign_id}
              onToggle={() => setExpandedCampaign(
                expandedCampaign === campaign.google_campaign_id ? null : campaign.google_campaign_id
              )}
            />
          ))}
        </div>
      )}
    </div>
  )
}
